import { useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "../../../components/tooltip";
import { useBrowserTitle } from "../../../hooks/useBrowserTitle";
import * as notifications from "../../../notifications/notifications";
import { PAGE_PATH } from "../../../path";
import api from "../../../services/api";
import { consts } from "../../../shim/jsbridge";
import { PAGE_TITLE } from "../../../titles";
import { convertBytesToMB, getSizeInBytes } from "../../../utils";
import LoadingAndErrorHandling from "../../commons/LoadingAndErrorHandling";
import useGetRulesetTestResults from "../hooks/useGetRulesetTestResults";
import {
  yaraQueryKey,
  YARA_DATA,
  YARA_REVISION_DETAILS,
  YARA_REVISION_HISTORY_FOR_RULESET,
  YARA_SAMPLES
} from "../queryKeys";
import YaraConfirmModal from "./YaraConfirmModal";
import YaraEditor from "./YaraEditor";
import YaraEditorModal from "./YaraEditorModal";
import YaraHeaderControls from "./YaraHeaderControls";
import YaraHistoryPanel from "./YaraHistoryPanel";
import YaraInfoPanel from "./YaraInfoPanel";
import YaraRulesetNameInput from "./YaraRulesetNameInput";
import useYaraEditorReducer from "./useYaraEditorReducer";

const { data_upload_max_memory_size: dataUploadMaxMemorySize } = consts;

const showNotification = (msg, level = "error") => {
  notifications.show({
    sticky: false,
    level,
    message: msg
  });
};
const showNonEditorErrors = (errors = []) => {
  errors.forEach(err => showNotification(err));
};

const handleTooLargeContent = (contentSizeInBytes, callback) => {
  const limitSizeInMB = convertBytesToMB(dataUploadMaxMemorySize);
  const overflow = contentSizeInBytes - dataUploadMaxMemorySize;
  callback({
    show: true,
    msg: `Limit is ${limitSizeInMB} ${"megabyte".pluralize(
      limitSizeInMB
    )}. You are over the limit by ${overflow} ${"character".pluralize(
      overflow
    )}`
  });
};

const YaraEditorWrapper = ({
  id,
  canUserPublishToSdm = false,
  show = false,
  onClose,
  onSaveAndClose,
  onRunNewTestClick,
  onRetestClick
}) => {
  const [editorDirty, setEditorDirty] = useState(false);
  const [modalState, actions] = useYaraEditorReducer(id);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toastData, setToastData] = useState({
    show: false,
    msg: ""
  });
  const queryClient = useQueryClient();
  const hideMainModal = rulesetId =>
    onClose(rulesetId || modalState.activeRuleId);
  const hideConfirmModal = () => setShowConfirmModal(false);
  const resetToast = () =>
    setToastData(prevState => ({
      ...prevState,
      show: false
    }));

  const editorRef = useRef();
  const { latest } = useGetRulesetTestResults(modalState.activeRuleId);

  const navigate = useNavigate();

  const isEditMode = !!modalState.ruleId;
  const hasInputErrors =
    !modalState.rulesetName ||
    modalState.rulesetNameError ||
    modalState.isEditorEmpty;

  const {
    data: fetchData,
    isLoading,
    isSuccess,
    error
  } = useQuery({
    queryKey: yaraQueryKey.fetchRule(modalState.ruleId),
    queryFn: () => api.yaraFetchRule({ id: modalState.ruleId }),
    select: ({ data }) => data,
    enabled: isEditMode
  });
  useEffect(() => {
    if (isSuccess && fetchData) {
      actions.ruleFetch(fetchData);
    }
  }, [actions, fetchData, isSuccess]);

  const { details, history } = fetchData || {};

  const processInvalidResponse = respData => {
    const { errors, message } = respData;
    actions.saveError(errors);
    setToastData({ show: true, msg: "Saving failed!" });
    showNonEditorErrors(errors);
    showNotification(message);
    return false;
  };
  const processValidResponse = respData => {
    actions.ruleSaved(respData);
    setToastData({ show: true, msg: "Saved successfully!" });
    return true;
  };
  const processResponse = response => {
    const respData = response.data;
    if (!respData.success) {
      showNotification(respData.message);
      setToastData({ show: true, msg: "Saving failed!" });
      return false;
    }
    return respData.valid
      ? processValidResponse(respData)
      : processInvalidResponse(respData);
  };

  const saveRuleset = (publish = false, onSaveSuccess = () => null) => {
    const content = editorRef.current.getValue();
    const contentSizeInBytes = getSizeInBytes(content);
    if (contentSizeInBytes > dataUploadMaxMemorySize) {
      handleTooLargeContent(contentSizeInBytes, setToastData);
      return;
    }

    const formData = new URLSearchParams();
    formData.append("name", modalState.rulesetName);
    if (canUserPublishToSdm) {
      formData.append("publish", publish);
    }
    formData.append("content", content);
    formData.append("enabled", modalState.enableRuleset);
    formData.append("ticloud", modalState.enableRulesetTiCloud);

    setToastData({ show: true, msg: "Saving..." });

    const invalidateYaraData = () => {
      queryClient.invalidateQueries({ queryKey: [YARA_DATA] });
      queryClient.invalidateQueries({ queryKey: [YARA_SAMPLES] });
      queryClient.invalidateQueries({
        queryKey: [YARA_REVISION_HISTORY_FOR_RULESET]
      });
      queryClient.invalidateQueries({ queryKey: [YARA_REVISION_DETAILS] });
    };

    if (isEditMode) {
      api
        .yaraUpdateRule({ data: formData, id: modalState.ruleId })
        .then(
          response =>
            processResponse(response) &&
            onSaveSuccess(response.data?.details?.id)
        )
        .then(invalidateYaraData)
        .catch(() => setToastData({ show: true, msg: "Updating failed!" }));
    } else {
      api
        .yaraSaveRule(formData)
        .then(
          response =>
            processResponse(response) &&
            onSaveSuccess(response.data?.details?.id)
        )
        .then(invalidateYaraData)
        .catch(() => setToastData({ show: true, msg: "Saving failed!" }));
    }
  };

  const saveEnabled =
    !hasInputErrors &&
    !modalState.rulesetRenameMode &&
    (!isEditMode || modalState.needsSave || details?.always_save);

  const handleRunNewTestClick = () => {
    navigate(
      `${PAGE_PATH.YARA}${PAGE_PATH.YARA_TESTRUN.replace(
        ":rulesetId",
        modalState.activeRuleId
      )}`
    );
    onRunNewTestClick?.();
  };
  const handleRetestClick = () => {
    const path = `${PAGE_PATH.YARA}${PAGE_PATH.YARA_TESTRUN_REANALYZE.replace(
      ":rulesetId",
      modalState.activeRuleId
    ).replace(":testrunId", latest.id.toString())}`;
    navigate(`${path}?retest=1`);
    onRetestClick?.();
  };

  // disabled retest button if there is no previous testrun or new editor value hasn't been saved
  const retestDisabled = !latest || modalState.needsSave || !editorDirty;

  useBrowserTitle(PAGE_TITLE.YARA_RULESET_EDIT(details?.name), true);

  const isSystemRule = details?.from_core || details?.from_paranoid;

  return (
    <YaraEditorModal
      title={"Yara Ruleset Editor"}
      className={"yara-modal-zindex"}
      backdropClassName={"yara-modal-zindex"}
      fullscreen
      show={show}
      onHide={() => {
        if (details?.from_core) {
          hideMainModal();
          return;
        }
        if (modalState.needsSave) {
          setShowConfirmModal(true);
          return;
        }
        hideMainModal();
      }}
    >
      <LoadingAndErrorHandling error={error} loading={isLoading}>
        <div className={"yara-editor-container"}>
          <YaraHeaderControls
            canUserPublishToSdm={canUserPublishToSdm}
            isPublishedToSdm={modalState.isPublishedToSdm}
            isCoreRuleset={details?.from_core}
            isParanoidRuleset={details?.from_paranoid}
            saveEnabled={saveEnabled}
            enableRuleset={modalState.enableRuleset}
            enableRulesetTiCloud={modalState.enableRulesetTiCloud}
            enableAutoCloudRetro={modalState.initAutoCloudRetro}
            onEnableRulesetClick={val => {
              actions.rulesetEnable(val);
            }}
            onEnableRulesetTiCloudClick={val => {
              actions.rulesetCloudEnable(val);
            }}
            onSaveClick={saveRuleset}
            onSaveCloseClick={() => {
              saveRuleset(true, rulesetId => {
                hideMainModal(rulesetId);
                onSaveAndClose(rulesetId);
              });
            }}
          />
          {isEditMode && (
            <YaraInfoPanel
              ruleId={modalState.ruleId}
              infoActive={details?.info_active}
              infoAdded={details?.info_added}
              infoEdited={details?.info_edited}
              onlineSourceCommitId={details?.online_source_commit_id}
            />
          )}
          <div className="flex width-100-pc flex-space-between">
            <YaraRulesetNameInput
              editMode={isEditMode}
              disabled={isSystemRule}
              rulesetName={modalState.rulesetName}
              onChange={(val, hasError) => {
                actions.rulesetNameChanged(val, hasError);
              }}
              onRenameModeChange={value => {
                actions.rulesetRenameMode(value);
              }}
            />
            <div style={{ marginTop: "3px" }} className="align-center flex">
              <Tooltip
                disabled={!isSystemRule}
                content="System rulesets cannot be tested."
                placement="top"
                id="testrunTooltip"
              >
                <Button
                  style={{ textTransform: "capitalize" }}
                  variant="outline-dark"
                  disabled={!modalState.activeRuleId || isSystemRule}
                  onClick={handleRunNewTestClick}
                >
                  Run New Test
                </Button>
              </Tooltip>
              <Tooltip
                disabled={!retestDisabled || !isSystemRule}
                content={
                  isSystemRule
                    ? "System rulesets cannot be tested."
                    : "Save changes before retesting."
                }
                placement="top"
                id="retestTooltip"
              >
                <Button
                  disabled={retestDisabled || isSystemRule}
                  style={{ textTransform: "capitalize", marginLeft: "10px" }}
                  onClick={handleRetestClick}
                >
                  Retest
                </Button>
              </Tooltip>
            </div>
          </div>
          {isEditMode && (
            <YaraHistoryPanel
              selectedRuleId={modalState.ruleId}
              historyItems={history}
              onItemClick={ruleId => {
                setEditorDirty(true);
                actions.ruleSelected(ruleId);
              }}
            />
          )}
          <YaraEditor
            error={modalState.editorError}
            warnings={modalState.warnings}
            content={modalState.initEditorValue}
            readOnly={details?.is_editing_disabled}
            ref={editorRef}
            onChange={value => {
              setEditorDirty(true);
              actions.editorValueChanged(value);
            }}
          />
        </div>
      </LoadingAndErrorHandling>

      <YaraConfirmModal
        canUserPublishToSdm={canUserPublishToSdm}
        isPublishedToSdm={modalState.isPublishedToSdm}
        isEditMode={isEditMode}
        show={showConfirmModal}
        rulesetName={modalState.rulesetName}
        onHide={() => hideConfirmModal()}
        onClickNo={() => {
          hideConfirmModal();
          hideMainModal();
        }}
        onClickYes={() => {
          hideConfirmModal();
          saveRuleset(
            canUserPublishToSdm && modalState.isPublishedToSdm,
            rulesetId => {
              hideMainModal(rulesetId);
              onSaveAndClose(rulesetId);
            }
          );
        }}
        onClickCancel={() => hideConfirmModal()}
      />

      <ToastContainer className="p-3" position="top-center">
        <Toast
          show={!!toastData.show}
          onClose={resetToast}
          autohide
          delay={2000}
          className="yara-toast"
        >
          <Toast.Body>{toastData.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </YaraEditorModal>
  );
};

YaraEditorWrapper.propTypes = {
  id: PropTypes.number,
  canUserPublishToSdm: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveAndClose: PropTypes.func,
  onRunNewTestClick: PropTypes.func,
  onRetestClick: PropTypes.func
};

export default YaraEditorWrapper;
